import { createEffect, createMemo, createSignal, onCleanup } from 'solid-js';
import { cpu, mem, gpu, ollama, llamacpp, llamacppTps, llamacppTpsByServer, pause } from '../state/metrics';
import { Cockpit } from '../widgets/cockpit';
import { CpuWidget } from '../widgets/cpu-widget';
import { MemoryWidget } from '../widgets/memory-widget';
import { OllamaCard } from '../widgets/ollama-card';
import { LlamacppCard } from '../widgets/llamacpp-card';
import { HealthSummary } from '../widgets/health-summary';
import { deriveFindings } from '../widgets/health-derive';

/**
 * Composes the HUD dashboard: CPU + Memory first, then the cockpit (model + GPU)
 * across both columns, then the runner cards, then the health line.
 * Layout truth: docs/design/2026-08-02-redesign-e-stark-controls.html.
 */
export function Layout() {
  const [nowMs, setNowMs] = createSignal(Date.now());
  const t = setInterval(() => setNowMs(Date.now()), 1000);
  onCleanup(() => clearInterval(t));

  // Timestamp of the most recent frame of any kind. Reset by the effect below on every
  // change to cpu/mem/gpu/ollama — a reference change on any of those signals only ever
  // happens when dispatchFrame wrote a fresh sample, so this can't be fooled by the 1s
  // UI tick above (which changes nowMs every second regardless of whether data arrived).
  const [lastFrameMs, setLastFrameMs] = createSignal(Date.now());
  createEffect(() => {
    cpu();
    mem();
    gpu();
    ollama();
    setLastFrameMs(Date.now());
  });

  // True once any metrics source has produced at least one sample — distinguishes "the
  // machine is genuinely healthy" from "we haven't heard from it yet", which both look
  // like zero findings to deriveFindings.
  const hasTelemetry = () => cpu().length > 0 || mem().length > 0 || gpu() != null || ollama() != null;
  const updatedSecondsAgo = () => Math.max(0, Math.floor((nowMs() - lastFrameMs()) / 1000));

  const findings = createMemo(() =>
    deriveFindings({ cpu: cpu().at(-1), mem: mem().at(-1), gpu: gpu(), ollama: ollama() }, nowMs()),
  );

  return (
    <main class="mx-auto w-full max-w-[1180px] p-5 pb-16 2xl:max-w-[1600px]">
      <div
        data-grid="dashboard"
        class="grid grid-cols-1 gap-4 min-[880px]:grid-cols-2"
        classList={{ 'dashboard-paused': pause().paused }}
      >
        <CpuWidget cpu={cpu} />
        <MemoryWidget mem={mem} />
        <Cockpit gpu={gpu} ollama={ollama} llamacpp={llamacpp} tps={llamacppTps} tpsByServer={llamacppTpsByServer} />
        <LlamacppCard data={llamacpp} gpu={gpu} />
        <OllamaCard data={ollama} />
      </div>
      <HealthSummary findings={findings} hasTelemetry={hasTelemetry} updatedSecondsAgo={updatedSecondsAgo} />
    </main>
  );
}
